import React, { useState } from 'react';
import classNames from 'classnames';

import iconExpandDown from '../../assets/img/icon-chevron.svg';
import { formatFinancialNumber } from '../../util/helpers';
import ClickableDiv from '../ClickableDiv/ClickableDiv';
import ClickableImage from '../ClickableImage/ClickableImage';

import FinancialsDivSubRow from './FinancialsDivSubRow';

import styles from './FinancialsDivRow.module.scss';

type Props = {
  data: Array<number | string>; // Data for the main row

  // Data for sub-rows associated with the main row
  // Each inner array represents one sub-row
  subData?: Array<Array<number | string>>;

  // Additional nested data for each sub-row (third level of rows)
  // For every index in `subData`, you can provide an array of extra rows
  // The outer array aligns with subData indexes
  extraSubData?: Array<Array<Array<number | string>>>;

  // Enables click interactions and toggling of main row
  // If false, only the main row will be displayed
  isClickable?: boolean;
};

const FinancialsDivRow = ({
  data,
  subData,
  extraSubData,
  isClickable,
}: Props): JSX.Element => {
  const [isSelectTabOpened, setIsSelectTabOpened] = useState(false);
  const [isSelectSubTabOpened, setIsSelectSubTabOpened] = useState<Array<string | number>>([]);

  const toggleInArray = (arr: Array<string | number>, value: string | number) => (arr.includes(value)
    ? arr.filter(item => item !== value)
    : [...arr, value]);

  const renderCell = (
    showImage: boolean,
    cell: string | number,
    isSubCell?: boolean,
    isExtraSubCell?: boolean,
    isSubImage?: boolean,
    subImageCondition?: boolean,
  ) => (
    <>
      {showImage
        ? (
          <div className={styles.firstClickableCell}>
            {isSubImage ? (
              <ClickableImage
                alt=""
                src={iconExpandDown}
                className={subImageCondition ? styles.iconExpand : styles.iconShowLess}
              />
            ) : (
              <ClickableImage
                alt=""
                src={iconExpandDown}
                className={isSelectTabOpened ? styles.iconExpand : styles.iconShowLess}
              />
            )}
            <div className={classNames({ [styles.subCell]: isSubCell }, { [styles.extraSubCell]: isExtraSubCell })}>
              {formatFinancialNumber(cell)}
            </div>
          </div>
        )
        : (
          <div className={classNames(styles.cell,
            { [styles.subCell]: isSubCell }, { [styles.extraSubCell]: isExtraSubCell })}
          >
            {formatFinancialNumber(cell)}
          </div>
        )}
    </>
  );

  return (
    !isClickable
      ? (
        <>
          <div className={styles.row}>
            {data?.map(((cell, index) => (
              // eslint-disable-next-line
              <React.Fragment key={`${cell}${index}`}>
                {renderCell(false, cell)}
              </React.Fragment>
            )))}
          </div>
        </>
      ) : (
        <>
          <ClickableDiv
            onClick={() => setIsSelectTabOpened(!isSelectTabOpened)}
            className={classNames(styles.row, styles.clickableRow)}
          >
            {data?.map(((cell, index) => (
              // eslint-disable-next-line
              <React.Fragment key={`${cell}${index}`}>
                {renderCell(index === 0, cell)}
              </React.Fragment>
            )))}
          </ClickableDiv>
          {isSelectTabOpened && subData && !extraSubData && (
            <>
              {subData.map((row, index) => (
                // eslint-disable-next-line
                <div key={`${row[index]}${index}`} className={styles.row}>
                  {subData[index].map(((cell, subIndex) => (
                    // eslint-disable-next-line
              <React.Fragment key={`${row}${cell}${index}${subIndex}`}>
                {renderCell(false, cell, true)}
              </React.Fragment>
                  )))}
                </div>
              ))}
            </>
          )}
          {isSelectTabOpened && subData && extraSubData && (
            <>
              {subData.map((row, index) => (
                // eslint-disable-next-line
                <React.Fragment key={`${row[index]}${index}`}>
                  {extraSubData[index]?.length > 0 ? (
                    <>
                      <ClickableDiv
                        className={classNames(styles.row, styles.clickableRow)}
                        onClick={() => setIsSelectSubTabOpened((prev) => toggleInArray(prev, row[0]))}
                      >
                        {subData[index].map(((cell, subIndex) => (
                    // eslint-disable-next-line
                    <React.Fragment key={`${cell}${subIndex}`}>
                      {renderCell(
                        subIndex === 0 && index < extraSubData.length,
                        cell,
                        true,
                        false,
                        true,
                        isSelectSubTabOpened.includes(row[0]),
                      )}
                    </React.Fragment>
                        )))}
                      </ClickableDiv>
                      <FinancialsDivSubRow
                        extraSubData={extraSubData[index]}
                        isVisible={isSelectSubTabOpened.includes(row[0])}
                      />
                    </>
                  ) : (
                  // eslint-disable-next-line
                    <React.Fragment key={`${row[index]}${index}`}>
                      <div className={styles.row}>
                        {subData[index].map(((cell, subIndex) => (
                          // eslint-disable-next-line
                          <React.Fragment key={`${cell}${subIndex}`}>
                            {renderCell(subIndex === 0 && extraSubData[index]?.length > 0, cell, true, false)}
                          </React.Fragment>
                        )))}
                      </div>
                    </React.Fragment>
                  )}
                </React.Fragment>
              ))}
            </>
          )}
        </>
      )

  );
};

export default FinancialsDivRow;
